import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, AlertCircle, Loader2, Heart, Facebook, MessageCircle, Twitter, Copy, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

/* --------------------------------------------------------------------------
   PaymentSuccess — thank-you page with rich share options for donations.
-------------------------------------------------------------------------- */

const SHARE_TEXT = "Am susținut misiunea Tati și Sofia — pentru copiii rupți de un părinte care îi iubește. Alătură-te!";

export default function PaymentSuccess({ type = "donation" }) {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking|paid|failed
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!session_id) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled || n > 8) { if (!cancelled) setStatus("failed"); return; }
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        setData(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("failed"); return; }
        setTries(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTries(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id]);

  const messages = {
    donation: { ok: "Mulțumim din suflet!", desc: "Prin donația ta, un părinte găsește informație și un copil aude adevărul. Nu ești singur în această misiune." },
    booking: { ok: "Programare confirmată", desc: "Vei primi pe WhatsApp confirmarea finală. Mulțumim că ai ales să vorbești cu Gabriel." },
    shop: { ok: "Comandă confirmată", desc: "Vei primi pe email confirmarea și link-urile de descărcare pentru produsele digitale." },
  };
  const m = messages[type] || messages.donation;

  const homeUrl = typeof window !== "undefined" ? window.location.origin : "https://tatisisofia.ro";

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(homeUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${homeUrl}`)}`,
    twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(homeUrl)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(homeUrl);
      setCopied(true);
      toast.success("Link copiat!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Nu am putut copia linkul."); }
  };

  if (status === "checking") {
    return (
      <div data-testid={`page-payment-success-${type}`} className="max-w-xl mx-auto px-4 py-20 md:py-32 text-center">
        <Loader2 className="w-12 h-12 text-[#D4A63D] mx-auto mb-6 animate-spin" />
        <h1 className="font-display text-3xl text-[#F8F8F4]">Verificăm plata...</h1>
        <p className="text-[#A0AABF] mt-3 text-sm">Încercare {tries + 1}/8</p>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div data-testid={`page-payment-success-${type}`} className="max-w-xl mx-auto px-4 py-20 md:py-32 text-center">
        <AlertCircle className="w-12 h-12 text-[#C83A3A] mx-auto mb-6" />
        <h1 className="font-display text-3xl text-[#F8F8F4]">Plata nu a fost confirmată</h1>
        <p className="text-[#A0AABF] mt-3">Te rugăm să încerci din nou sau să ne contactezi.</p>
        <Link to="/" className="btn-ghost-light rounded-sm px-6 py-3 mt-6 inline-block">Înapoi</Link>
      </div>
    );
  }

  // PAID
  if (type !== "donation") {
    return (
      <div data-testid={`page-payment-success-${type}`} className="max-w-xl mx-auto px-4 py-20 md:py-32 text-center">
        <div className="w-16 h-16 rounded-full bg-[#3BA76D]/20 grid place-items-center mx-auto mb-6">
          <Check className="w-9 h-9 text-[#3BA76D]" />
        </div>
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-3">Confirmat</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#F8F8F4]">{m.ok}</h1>
        <p className="text-[#A0AABF] mt-5 text-base md:text-lg max-w-md mx-auto leading-relaxed">{m.desc}</p>
        {data?.amount && <p className="font-display text-3xl gold-text mt-6">{(data.amount/100).toFixed(2)} {data.currency?.toUpperCase()}</p>}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-gold rounded-sm px-6 py-3 text-sm uppercase tracking-widest">Pagina principală</Link>
          <Link to="/comunitate" className="btn-ghost-light rounded-sm px-6 py-3 text-sm uppercase tracking-widest">Vezi comunitatea</Link>
        </div>
      </div>
    );
  }

  // Donation success — cinematic
  const amountDisplay = data?.amount ? (data.amount / 100).toFixed(0) : null;

  return (
    <div data-testid="page-payment-success-donation" className="relative min-h-[80vh] py-16 md:py-24 px-4 overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#D4A63D]/10 blur-3xl" />
        <div className="absolute -bottom-32 left-1/4 w-72 h-72 rounded-full bg-[#E89BB8]/10 blur-3xl" />
      </div>

      <div className="max-w-3xl mx-auto text-center">
        {/* Icon */}
        <div className="relative inline-flex mb-6">
          <div className="absolute inset-0 rounded-full bg-[#D4A63D]/25 blur-2xl animate-pulse" />
          <div className="relative w-20 h-20 rounded-full bg-gradient-to-b from-[#F2C053] to-[#D4A63D] grid place-items-center shadow-[0_10px_40px_rgba(212,166,61,0.4)]">
            <Heart className="w-10 h-10 text-[#0A1020] fill-[#0A1020]" />
          </div>
        </div>

        <p className="text-[10px] tracking-[0.35em] text-[#D4A63D] uppercase font-bold mb-3">Confirmat · cu iubire</p>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-transparent bg-clip-text bg-gradient-to-b from-[#F2E1B0] via-[#D4A63D] to-[#8F6E1E]">
          {m.ok}
        </h1>

        {amountDisplay && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4A63D]/40 bg-[#D4A63D]/10">
            <span className="font-display text-2xl gold-text">{amountDisplay}</span>
            <span className="text-[#F8F8F4]/80 text-sm">{(data.currency || "ron").toUpperCase()}</span>
            <span className="text-[#A0AABF] text-xs">· trimiși cu inimă deschisă</span>
          </div>
        )}

        <p className="text-[#F8F8F4]/90 mt-6 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
          {m.desc}
        </p>

        {/* Impact block */}
        <div className="mt-10 mx-auto max-w-2xl rounded-md border border-white/10 bg-white/[0.03] p-6 md:p-8 text-left">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A63D] font-bold flex items-center gap-2 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Impactul contribuției tale
          </div>
          <ul className="space-y-3 text-sm text-[#F8F8F4]/90 leading-relaxed">
            <li className="flex items-start gap-3">
              <Check className="w-4 h-4 text-[#3BA76D] shrink-0 mt-0.5" />
              <span>Sprijini mentenanța portalului și dezvoltarea unor instrumente noi (SCAN AP, hărți, ghiduri).</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-4 h-4 text-[#3BA76D] shrink-0 mt-0.5" />
              <span>Ajuți la producerea de articole și materiale video care ajung la părinți aflați în suferință.</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-4 h-4 text-[#3BA76D] shrink-0 mt-0.5" />
              <span>Contribui la campanii de conștientizare care fac vocile copiilor să fie auzite.</span>
            </li>
          </ul>
        </div>

        {/* Share block */}
        <div className="mt-10">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A0AABF] font-bold mb-4">
            Multiplică impactul · spune și altora
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <a
              href={shareLinks.facebook}
              target="_blank" rel="noreferrer"
              data-testid="share-facebook"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#3B5998]/50 text-[#8DA5D6] hover:bg-[#3B5998]/10 text-xs font-bold tracking-wider"
            >
              <Facebook className="w-4 h-4" /> FACEBOOK
            </a>
            <a
              href={shareLinks.whatsapp}
              target="_blank" rel="noreferrer"
              data-testid="share-whatsapp"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 text-xs font-bold tracking-wider"
            >
              <MessageCircle className="w-4 h-4" /> WHATSAPP
            </a>
            <a
              href={shareLinks.twitter}
              target="_blank" rel="noreferrer"
              data-testid="share-twitter"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/25 text-white hover:bg-white/5 text-xs font-bold tracking-wider"
            >
              <Twitter className="w-4 h-4" /> X / TWITTER
            </a>
            <button
              onClick={copyLink}
              data-testid="share-copy"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border text-xs font-bold tracking-wider transition ${
                copied
                  ? "border-[#3BA76D] text-[#3BA76D] bg-[#3BA76D]/10"
                  : "border-[#D4A63D]/50 text-[#D4A63D] hover:bg-[#D4A63D]/10"
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "COPIAT" : "COPIAZĂ LINK"}
            </button>
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" data-testid="success-home" className="btn-gold rounded-sm px-7 py-3.5 text-xs uppercase tracking-widest font-bold">
            PAGINA PRINCIPALĂ
          </Link>
          <Link to="/comunitate" data-testid="success-community" className="rounded-sm px-7 py-3.5 text-xs uppercase tracking-widest font-bold border border-white/15 text-white hover:bg-white/5 inline-flex items-center justify-center gap-2">
            VEZI COMUNITATEA
          </Link>
          <a
            href="https://restart.tatisisofia.ro/"
            target="_blank" rel="noreferrer"
            data-testid="success-restart"
            className="rounded-sm px-7 py-3.5 text-xs uppercase tracking-widest font-bold border border-[#E89BB8]/40 text-[#E89BB8] hover:bg-[#E89BB8]/10 inline-flex items-center justify-center gap-2"
          >
            RESTART <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <p className="mt-10 text-xs text-[#6C7A9C] italic max-w-md mx-auto">
          „Niciun copil nu ar trebui să fie rupt de un părinte care îl iubește.” — Tati și Sofia
        </p>
      </div>
    </div>
  );
}
